import { isNil } from 'lodash';
import { useContext, useMemo } from 'react';
import { WorkflowComponentsContext } from '@shared/scenes/entity/context/WorkflowComponentsContext';
import { getComponentLabelWithFallback } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface InputButtonTarget {
  /** The component the button writes to, or undefined when it points at nothing. */
  component?: API.AnyComponent;
  /** That component's label in this context, for naming it in the button's read-only tooltip. */
  label: string;
  /** Whether that component is read only in this context, so the button must not open its form. */
  isReadOnly: boolean;
}

/**
 * Resolves the component an INPUT_BUTTON in a form writes to, with the form's overrides applied.
 * A form is misconfigured if it points a button at a read-only component, but nothing stops an
 * admin from saving one, so the button has to resolve the target's effective read-only state
 * itself rather than trust the raw workflow template.
 *
 * {@link WorkflowComponentsContext} already holds the form's components with its view template's
 * overrides merged in. Forms rendered without that provider - bulk update, for one - fall back to
 * the workflow template's own read-only flag.
 */
export function useFormInputButtonTarget(
  workflowTemplate: API.WorkflowTemplateFragment | undefined,
  inputComponentId: string,
): InputButtonTarget {
  const formComponents = useContext(WorkflowComponentsContext);

  return useMemo(() => {
    const component =
      formComponents?.find(item => item.id === inputComponentId) ??
      workflowTemplate?.components.find(item => item.id === inputComponentId);
    return toTarget(component, isReadOnlyComponent(component));
  }, [formComponents, inputComponentId, workflowTemplate?.components]);
}

/**
 * The collection view counterpart of {@link useFormInputButtonTarget}.
 *
 * Deliberately ignores the collection view template. Collection views are not forms, so they are
 * always saved with `defaultReadonlyComponents: true` and every component in them reads as read
 * only in view - the input button is the exception, since it exists to edit one field without
 * opening the card form. A card view template marking the component read only does still disable
 * the button, but that is the form path ({@link useFormInputButtonTarget}); a collection view
 * template never does, which leaves only the workflow template's own read-only flag to check here.
 */
export function useCollectionViewInputButtonTarget(
  workflowTemplate: API.WorkflowTemplateFragment | undefined,
  inputComponentId: string,
): InputButtonTarget {
  return useMemo(() => {
    const component = workflowTemplate?.components.find(item => item.id === inputComponentId);
    return toTarget(component, isReadOnlyComponent(component));
  }, [inputComponentId, workflowTemplate?.components]);
}

function isReadOnlyComponent(component: API.AnyComponent | undefined): boolean {
  return !isNil(component) && 'readonly' in component && component.readonly === true;
}

function toTarget(component: API.AnyComponent | undefined, isReadOnly: boolean): InputButtonTarget {
  return {
    component,
    label: isNil(component) ? '' : getComponentLabelWithFallback(component),
    isReadOnly,
  };
}
